import React, { useEffect, useState, useRef } from 'react';
import axios from "axios";
import toast, { Toaster } from 'react-hot-toast';
import MUIDataTable from 'mui-datatables';
import CircularProgress from "@mui/material/CircularProgress";

const Document = () => {

    const baseUrl = localStorage.getItem('API_URL');
    const token = localStorage.getItem('user_token');

    const [documentData, setDocumentData] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [count, setCount] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const searchTimeoutRef = useRef(null);

    // data table state
    const [dataTable, setDataTable] = useState({
        page: 0,
        size: 20,
        search: '',
        order: null,
    });

    const setTable = (tableState) => {
        setDataTable({
            page: tableState.page,
            search: tableState.searchText || '',
            size: tableState.rowsPerPage,
            order: tableState.sortOrder
        });
    };

    const options = {
        tableBodyHeight: '62vh',
        filterType: 'dropdown',
        filter: false,
        responsive: 'vertical',
        serverSide: true,
        count: count,
        download: false,
        jumpToPage: true,
        selectableRows: 'none',
        print: false,
        rowsPerPage: rowsPerPage,
        rowsPerPageOptions: [10, 20, 30, 50, 100],
        searchText: searchText,
        setTableProps: () => {
            return {
                size: 'small',
            };
        },
        onTableChange: (action, tableState) => {
            switch (action) {
                case 'changePage':
                    getTicketCollections(tableState.page, tableState.sortOrder, tableState.searchText || '', tableState.rowsPerPage);
                    setTable(tableState);
                    break;
                case 'sort':
                    getTicketCollections(tableState.page, tableState.sortOrder, tableState.searchText || '', tableState.rowsPerPage);
                    setTable(tableState);
                    break;
                case 'changeRowsPerPage':
                    setRowsPerPage(tableState.rowsPerPage);
                    getTicketCollections(tableState.page, tableState.sortOrder, tableState.searchText || '', tableState.rowsPerPage);
                    setTable(tableState);
                    break;
                case 'search':
                    // Clear previous timeout
                    if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current);
                    }
                    // Update search text immediately to keep input focused
                    setSearchText(tableState.searchText || '');
                    // Debounce the API call
                    searchTimeoutRef.current = setTimeout(() => {
                        getTicketCollections(0, tableState.sortOrder, tableState.searchText || '', tableState.rowsPerPage);
                        setTable({
                            ...tableState,
                            page: 0, // Reset to first page on search
                            searchText: tableState.searchText || ''
                        });
                    }, 500); // 500ms debounce
                    break;
                default:
                    break;
            }
        },
    };

    const columns = [
        {
            label: '#',
            name: 'index',
            options: {
                sort: false,
                filter: false,
                customBodyRenderLite: (dataIndex, rowIndex) => {
                    if (documentData === undefined) return;
                    return (
                        <p className='m-0'>{dataIndex + 1 + (dataTable.page * dataTable.size)}</p>
                    );
                }
            }
        },
        {
            label: 'Ref',
            name: 'ref',
            options: {
                sort: true,
                filter: false,
            }
        },
        {
            label: 'Ticket Number',
            name: 'ticketNumber',
            options: {
                sort: false,
                filter: false,
            }
        },
        {
            label: 'Branch Name',
            name: 'branchName',
            options: {
                sort: false,
                filter: false,
            }
        },
        {
            label: 'Category',
            name: 'category',
            options: {
                sort: false,
                filter: false,
            }
        },
        {
            label: 'Ticket ID',
            name: 'ticketId',
            options: {
                sort: false,
                filter: false,
            }
        },
        {
            label: 'Mobile',
            name: 'mobile',
            options: {
                sort: false,
                filter: false,
                customBodyRender: (value) => {
                    return value || 'N/A';
                }
            }
        },
        {
            label: 'Status',
            name: 'status',
            options: {
                sort: true,
                filter: false,
                customBodyRender: (value) => {
                    if (value === 0) {
                        return <span className="badge badge-warning">Pending</span>;
                    } else if (value === 1) {
                        return <span className="badge badge-success">Accepted</span>;
                    } else if (value === 2) {
                        return <span className="badge badge-danger">Rejected</span>;
                    }
                    return <span className="badge badge-secondary">{value}</span>;
                }
            }
        },
        {
            label: 'Collected',
            name: 'collected',
            options: {
                sort: true,
                filter: false,
                customBodyRender: (value) => {
                    return value ? (
                        <span className="badge badge-success">Yes</span>
                    ) : (
                        <span className="badge badge-warning">No</span>
                    );
                }
            }
        },
        {
            label: 'SMS',
            name: 'sms',
            options: {
                sort: true,
                filter: false,
                customBodyRender: (value) => {
                    return value ? (
                        <span className="badge badge-info">Yes</span>
                    ) : (
                        <span className="badge badge-secondary">No</span>
                    );
                }
            }
        },
        {
            label: 'Added Time',
            name: 'addedTime',
            options: {
                sort: true,
                filter: false,
            }
        },
        {
            label: 'Updated Time',
            name: 'updatedTime',
            options: {
                sort: true,
                filter: false,
            }
        },
        {
            label: 'Collected Time',
            name: 'collectedTime',
            options: {
                sort: true,
                filter: false,
                customBodyRender: (value) => {
                    return value || 'N/A';
                }
            }
        },
        {
            label: 'Comment',
            name: 'comment',
            options: {
                sort: false,
                filter: false,
                customBodyRender: (value) => {
                    return value || 'N/A';
                }
            }
        },
    ];

    // call getTicketCollections - useEffect
    useEffect(() => {
        if (token !== null && baseUrl !== null) {
            getTicketCollections(0, null, '', rowsPerPage);
        }
        
        // Cleanup timeout on unmount
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // getTicketCollections - function
    const getTicketCollections = (page, sortOrder, search, size) => {
        setLoading(true);

        // Prepare sort object
        let sortObj = null;
        if (sortOrder && sortOrder.name) {
            sortObj = {
                name: sortOrder.name,
                direction: sortOrder.direction || 'DESC'
            };
        }

        axios({
            method: 'POST',
            url: `${baseUrl}/ticket-collection/get-all-paginated`,
            headers: {
                Authorization: token
            },
            data: {
                page: page,
                size: size,
                sort: sortObj,
                searchText: search || ''
            }
        }).then((res) => {
            console.log('getTicketCollections success', res.data);
            let data = res.data.data;
            if (data && data.content) {
                setDocumentData(data.content);
                setCount(data.totalElements);
            } else {
                setDocumentData([]);
                setCount(0);
            }
            setLoading(false);
        }).catch((error) => {
            console.log('getTicketCollections error', error);
            setLoading(false);
            setDocumentData([]);
            setCount(0);

            if (error.response !== undefined) {
                toast.remove();
                toast.error(error.response.data.message || 'Failed to fetch documents', {
                    position: 'bottom-center',
                });
            } else {
                toast.remove();
                toast.error('Something went wrong', {
                    position: 'bottom-center',
                });
            }
        });
    };

    return (
        <div className="container-fluid">
            <Toaster />
            {/* <div className="row clearfix">
                <div className="col-lg-12 col-md-12">
                    <div className="card">
                        <div className="body">
                            <h2>Documents</h2>
                            <p className="text-muted">Ticket Collection Management</p>
                        </div>
                    </div>
                </div>
            </div> */}

            <div className="row clearfix">
                <div className="col-lg-12 col-md-12">
                    <div className="card">
                        <div className="body">
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <CircularProgress />
                                    <p className="mt-3">Loading documents...</p>
                                </div>
                            ) : (
                                <MUIDataTable
                                    title="Documents"
                                    data={documentData}
                                    columns={columns}
                                    options={options}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Document;
